// lib/theme.ts — 3-state theme (System / Dark / Light) (spec §4.5).
// Pure functions so the logic is unit-testable; the hook wires them to the DOM.

export type Theme = 'system' | 'dark' | 'light';
export const THEMES: readonly Theme[] = ['system', 'dark', 'light'];
export const THEME_KEY = 'upload-theme';

export function isTheme(value: unknown): value is Theme {
  return typeof value === 'string' && (THEMES as readonly string[]).includes(value);
}

/** Whether dark mode is active for a given theme + system preference. */
export function resolveDark(theme: Theme, prefersDark: boolean): boolean {
  return theme === 'dark' || (theme === 'system' && prefersDark);
}

export function loadTheme(storage: Pick<Storage, 'getItem'>): Theme {
  const raw = storage.getItem(THEME_KEY);
  return isTheme(raw) ? raw : 'system';
}

export function saveTheme(storage: Pick<Storage, 'setItem'>, theme: Theme): void {
  storage.setItem(THEME_KEY, theme);
}

/** Apply the theme to a root element (toggles `dark` class + color-scheme). */
export function applyTheme(root: HTMLElement, theme: Theme, prefersDark: boolean): boolean {
  const dark = resolveDark(theme, prefersDark);
  root.classList.toggle('dark', dark);
  root.style.colorScheme = dark ? 'dark' : 'light';
  return dark;
}

/** Cycle order for the toggle button: system → light → dark → system. */
export function nextTheme(theme: Theme): Theme {
  const order: Theme[] = ['system', 'light', 'dark'];
  return order[(order.indexOf(theme) + 1) % order.length];
}

export function themeLabel(theme: Theme): string {
  return { system: 'System', dark: 'Dark', light: 'Light' }[theme];
}
